import { useCallback, useEffect, useMemo, useState } from 'react';
import { ProCard } from '@ant-design/pro-components';
import AdminProTable from '@/components/admin-pro-table';
import { App, Button, Flex, Form, Select, Tag, Typography } from 'antd';
import { useSetState } from 'ahooks';
import Permission, { useAccess } from '@/components/permission';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import AuditModal from '@/pages/mining-weekly-rewards/components/audit-modal';
import {
    formatAutoReleaseAt,
    formatDateTime,
    formatOutput,
    formatPeriodRange,
    getWeekPeriodLabel,
    isPendingStatus,
} from '@/pages/mining-weekly-rewards/utils';
import {
    miningWeeklyRewardApprovePeriod,
    miningWeeklyRewardGroupByWeek,
    normalizeWeekGroup,
    type MiningWeeklyRewardListParams,
    type MiningWeeklyRewardWeekGroup,
} from '@/services/mining-weekly-rewards';
import { resolveApiResult } from '@/utils/apiResponse';

import type { ProColumns } from '@ant-design/pro-components';
import type { PaginationProps } from 'antd';

const PERMISSION = 'Dramas_Mining-Weekly-Rewards';
const REVIEW_PERMISSION = 'Dramas_Mining-Weekly-Rewards_Review';
const DETAIL_PATH = '/dramas/mining-weekly-rewards/detail';

const DEFAULT_PAGINATION: PaginationProps = {
    current: 1,
    pageSize: 20,
};

type SearchFilters = Pick<MiningWeeklyRewardListParams, 'audit_status' | 'reward_type'>;

type PeriodModalState = {
    open: boolean;
    week?: MiningWeeklyRewardWeekGroup;
};

const pickSearchFilters = (values: Record<string, unknown>): SearchFilters => {
    const next: SearchFilters = {};
    (['reward_type', 'audit_status'] as const).forEach((key) => {
        const text = String(values[key] ?? '').trim();
        if (text) next[key] = text;
    });
    return next;
};

const PageMiningWeeklyRewards: React.FC = () => {
    const { message } = App.useApp();
    const { formatMessage } = useIntl();
    const access = useAccess();
    const navigate = useNavigate();

    const [pagination, setPagination] = useSetState<PaginationProps>({ ...DEFAULT_PAGINATION });
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [allData, setAllData] = useState<MiningWeeklyRewardWeekGroup[]>([]);
    const [searchFilters, setSearchFilters] = useState<SearchFilters>({});
    const [periodModal, setPeriodModal] = useState<PeriodModalState>({ open: false });
    const [searchForm] = Form.useForm();

    const canReview = access(REVIEW_PERMISSION);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const response = await miningWeeklyRewardGroupByWeek({
                ...searchFilters,
            });
            const { data } = response;

            if (data.code !== 200) {
                message.error(data.msg);
                return;
            }

            const rows = (data.data ?? []).map((item) => normalizeWeekGroup(item));
            setAllData(rows);

            const meta = data.meta;
            if (meta) {
                setPagination({
                    current: Number(meta.page) > 0 ? Number(meta.page) : 1,
                    pageSize: Number(meta.size) > 0 ? Number(meta.size) : DEFAULT_PAGINATION.pageSize,
                    total: meta.total ?? rows.length,
                });
            } else {
                setPagination((prev) => ({
                    ...prev,
                    current: 1,
                    total: rows.length,
                }));
            }
        } catch (error: any) {
            message.error(error?.response?.data?.msg || error?.message || 'Failed to fetch list');
        } finally {
            setLoading(false);
        }
    }, [message, searchFilters, setPagination]);

    const handleSearch = useCallback(() => {
        const nextFilters = pickSearchFilters(searchForm.getFieldsValue());
        setPagination((prev) => ({ ...prev, current: 1 }));
        setSearchFilters(nextFilters);
    }, [searchForm, setPagination]);

    const handleResetSearch = useCallback(() => {
        searchForm.resetFields();
        setPagination((prev) => ({ ...prev, current: 1 }));
        setSearchFilters({});
    }, [searchForm, setPagination]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const dataSource = useMemo(() => {
        const page = pagination.current ?? 1;
        const pageSize = pagination.pageSize ?? 20;
        const start = (page - 1) * pageSize;
        return allData.slice(start, start + pageSize);
    }, [allData, pagination.current, pagination.pageSize]);

    const openDetail = useCallback(
        (week: MiningWeeklyRewardWeekGroup) => {
            navigate(`${DETAIL_PATH}?week_n=${encodeURIComponent(String(week.week_n))}`, {
                state: { week },
            });
        },
        [navigate],
    );

    const openPeriodModal = useCallback((week: MiningWeeklyRewardWeekGroup) => {
        setPeriodModal({ open: true, week });
    }, []);

    const closePeriodModal = useCallback(() => {
        setPeriodModal({ open: false });
    }, []);

    const handleApprovePeriod = useCallback(
        async ({ auditBy, auditRemark }: { auditBy?: string; auditRemark?: string }) => {
            const week = periodModal.week;
            if (!week || week.week_n == null) return;

            setSubmitting(true);
            try {
                const { data } = await miningWeeklyRewardApprovePeriod({
                    weekN: Number(week.week_n),
                    ...(auditBy?.trim() ? { auditBy: auditBy.trim() } : {}),
                    ...(auditRemark?.trim() ? { auditRemark: auditRemark.trim() } : {}),
                });

                const result = resolveApiResult(data);
                if (!result.success) {
                    message.error(result.msg || 'Approve period failed');
                    return;
                }

                message.success(formatMessage({ id: 'c.operationCompleted' }));
                closePeriodModal();
                await fetchList();
            } catch (error: any) {
                message.error(error?.response?.data?.msg || error?.message || 'Approve period failed');
            } finally {
                setSubmitting(false);
            }
        },
        [closePeriodModal, fetchList, formatMessage, message, periodModal.week],
    );

    const auditStatusOptions = useMemo(
        () => [
            {
                value: '0',
                label: formatMessage({ id: 'miningWeeklyRewards.auditStatus.pending' }),
            },
            {
                value: '1',
                label: formatMessage({ id: 'miningWeeklyRewards.auditStatus.audited' }),
            },
        ],
        [formatMessage],
    );

    const rewardTypeOptions = useMemo(
        () => [
            {
                value: '0',
                label: formatMessage({ id: 'miningWeeklyRewards.rewardType.mining' }),
            },
            {
                value: '1',
                label: formatMessage({ id: 'miningWeeklyRewards.rewardType.invite' }),
            },
        ],
        [formatMessage],
    );

    const columns = useMemo<ProColumns<MiningWeeklyRewardWeekGroup>[]>(
        () => [
            {
                dataIndex: 'index',
                title: 'No.',
                valueType: 'index',
                width: 60,
            },
            {
                dataIndex: 'week_n',
                title: <FormattedMessage id="miningWeeklyRewards.period.weekN" />,
                width: 90,
                render: (_, record) => record.week_n ?? '-',
            },
            {
                key: 'range',
                title: <FormattedMessage id="miningWeeklyRewards.period.range" />,
                width: 140,
                render: (_, record) => formatPeriodRange(record.start_time, record.end_time),
            },
            {
                dataIndex: 'record_count',
                title: <FormattedMessage id="miningWeeklyRewards.period.userCount" />,
                width: 90,
                align: 'right',
                render: (_, record) =>
                    record.record_count == null || Number.isNaN(record.record_count)
                        ? '-'
                        : record.record_count,
            },
            {
                dataIndex: 'actual_output',
                title: <FormattedMessage id="miningWeeklyRewards.period.actualOutput" />,
                width: 150,
                align: 'right',
                render: (_, record) => formatOutput(record.actual_output),
            },
            {
                dataIndex: 'audit_status',
                title: <FormattedMessage id="miningWeeklyRewards.period.status" />,
                width: 110,
                render: (_, record) =>
                    isPendingStatus(record.audit_status) ? (
                        <Tag color="orange">
                            <FormattedMessage id="miningWeeklyRewards.status.pending" />
                        </Tag>
                    ) : (
                        <Tag color="green">
                            <FormattedMessage id="miningWeeklyRewards.status.approved" />
                        </Tag>
                    ),
            },
            {
                key: 'auto_release_at',
                title: <FormattedMessage id="miningWeeklyRewards.period.autoReleaseAt" />,
                width: 180,
                render: (_, record) => formatAutoReleaseAt(record.end_time),
            },
            {
                dataIndex: 'audit_at',
                title: <FormattedMessage id="miningWeeklyRewards.period.approvedAt" />,
                width: 180,
                render: (_, record) => formatDateTime(record.audit_at),
            },
            {
                key: 'action',
                title: <FormattedMessage id="miningWeeklyRewards.columns.action" />,
                width: 120,
                fixed: 'right',
                hideInTable: !canReview,
                render: (_, record) =>
                    isPendingStatus(record.audit_status) ? (
                        <Button
                            type="link"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                openPeriodModal(record);
                            }}
                        >
                            <FormattedMessage id="miningWeeklyRewards.actions.release" />
                        </Button>
                    ) : (
                        <Typography.Text type="secondary">
                            <FormattedMessage id="miningWeeklyRewards.actions.released" />
                        </Typography.Text>
                    ),
            },
        ],
        [canReview, openPeriodModal],
    );

    return (
        <Permission permission={PERMISSION}>
            <ProCard title={<FormattedMessage id="miningWeeklyRewards.title" />}>
                <Form form={searchForm} onFinish={handleSearch} style={{ marginBottom: 16 }}>
                    <Flex gap={16} wrap>
                        <Form.Item name="reward_type" style={{ marginBottom: 0 }}>
                            <Select
                                allowClear
                                options={rewardTypeOptions}
                                placeholder={formatMessage({
                                    id: 'miningWeeklyRewards.search.rewardType',
                                })}
                                style={{ width: 160 }}
                            />
                        </Form.Item>
                        <Form.Item name="audit_status" style={{ marginBottom: 0 }}>
                            <Select
                                allowClear
                                options={auditStatusOptions}
                                placeholder={formatMessage({
                                    id: 'miningWeeklyRewards.search.auditStatus',
                                })}
                                style={{ width: 160 }}
                            />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            <FormattedMessage id="b.search" />
                        </Button>
                        <Button onClick={handleResetSearch}>
                            <FormattedMessage id="b.reset" />
                        </Button>
                    </Flex>
                </Form>
                <AdminProTable<MiningWeeklyRewardWeekGroup>
                    bordered
                    columns={columns}
                    dataSource={dataSource}
                    loading={loading}
                    rowKey={(record) => String(record.week_n)}
                    search={false}
                    onRow={(record) => ({
                        onClick: () => openDetail(record),
                        style: { cursor: 'pointer' },
                    })}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        onChange: (page, pageSize) => {
                            setPagination({ current: page, pageSize: pageSize ?? 20 });
                        },
                    }}
                    options={{
                        reload: () => fetchList(),
                        density: true,
                        setting: true,
                    }}
                    scroll={{ x: 1300 }}
                    locale={{
                        emptyText: formatMessage({ id: 'miningWeeklyRewards.empty' }),
                    }}
                />
            </ProCard>
            <AuditModal
                open={periodModal.open}
                mode="period"
                periodLabel={getWeekPeriodLabel(periodModal.week)}
                loading={submitting}
                onCancel={closePeriodModal}
                onSubmit={handleApprovePeriod}
            />
        </Permission>
    );
};

export default PageMiningWeeklyRewards;
